import { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, Navigate } from 'react-router-dom';
import { gsap } from 'gsap';
import {
  Home,
  Search,
  Briefcase,
  FileText,
  MessageSquare,
  Users,
  Shield,
  User,
  ChevronLeft,
  ChevronRight,
  LogOut,
  AlertCircle,
  LucideWrench,
  Menu,
  X,
  BookOpen,
} from 'lucide-react';
import { MessagesSquare } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import NotificationsBell from '../common/NotificationsBell';

const navItems = {
  customer: [
    { name: 'Home', icon: Home, path: '/dashboard/customer' },
    { name: 'Find Nannies', icon: Search, path: '/dashboard/customer/nannies' },
    { name: 'My Offers', icon: Briefcase, path: '/dashboard/customer/offers' },
    { name: 'Current Contract', icon: FileText, path: '/dashboard/customer/contract/current' },
    { name: 'Messages', icon: MessageSquare, path: '/dashboard/messages' },
    { name: 'Support', icon: AlertCircle, path: '/dashboard/support' },
    { name: 'Resources', icon: FileText, path: '/dashboard/resources' },
  ],
  nanny: [
    { name: 'Home', icon: Home, path: '/dashboard/nanny' },
    { name: 'Complete Profile', icon: User, path: '/dashboard/nanny/profile', highlight: true },
    { name: 'My Offers', icon: Briefcase, path: '/dashboard/nanny/offers' },
    { name: 'Current Job', icon: FileText, path: '/dashboard/nanny/contract/current' },
    { name: 'Messages', icon: MessageSquare, path: '/dashboard/messages' },
    { name: 'Support', icon: AlertCircle, path: '/dashboard/support' },
    { name: 'Resources', icon: FileText, path: '/dashboard/resources' },
  ],
  agent: [
    { name: 'Dashboard', icon: Home, path: '/dashboard/agent' },
    { name: 'My Nannies', icon: Users, path: '/dashboard/agent/nannies' },
    { name: 'Messages', icon: MessageSquare, path: '/dashboard/messages' },
    { name: 'Support', icon: AlertCircle, path: '/dashboard/support' },
  ],
  admin: [
    { name: 'Overview', icon: Home, path: '/dashboard/admin' },
    { name: 'Users', icon: Users, path: '/dashboard/admin/users' },
    { name: 'Disputes', icon: AlertCircle, path: '/dashboard/admin/disputes' },
    { name: 'Complaints', icon: MessageSquare, path: '/dashboard/admin/complaints' },
    { name: 'App Settings', icon: LucideWrench, path: '/dashboard/admin/app-settings' },
    { name: 'Create Admin', icon: Shield, path: '/dashboard/admin/create-admin' },
    { name: 'Resources', icon: BookOpen, path: '/dashboard/admin/resources' },
  ],
};

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout, loading } = useAuth();
  const location = useLocation();

  const role = user?.profile_type || 'customer';

  const menu = (navItems[role] || navItems.customer).filter((item) => {
    if (role === 'nanny' && item.path === '/dashboard/nanny/profile') {
      return !(user?.is_profile_complete === true || user?.is_profile_complete === '1');
    }
    return true;
  });

  useEffect(() => {
    gsap.fromTo(
      '.page-content',
      { opacity: 0, x: 24 },
      { opacity: 1, x: 0, duration: 0.35, ease: 'power2.out' }
    );
  }, [location.pathname]);

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const getUserInitial = () => user?.first_name?.[0]?.toUpperCase() || 'U';

  const userImage =
    user?.profile_picture && !user.profile_picture.includes('default-avatar')
      ? `${process.env.REACT_APP_IMAGE_URL}${user.profile_picture}`
      : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="rounded-2xl bg-white px-6 py-4 shadow-sm border border-gray-100">
          <div className="animate-pulse text-base font-medium text-gray-600">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const desktopSidebarWidth = collapsed ? 'lg:w-24' : 'lg:w-72';
  const desktopContentOffset = collapsed ? 'lg:ml-24' : 'lg:ml-72';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[1px] lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 flex flex-col border-r border-gray-200 bg-white
          transition-all duration-300 ease-out
          w-72 ${desktopSidebarWidth}
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
      >
        {/* Brand */}
        <div
          className={`border-b border-gray-200 ${
            collapsed ? 'h-20 px-3' : 'h-20 px-5'
          } flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}
        >
          <Link
            to="/"
            className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} min-w-0`}
            onClick={() => setSidebarOpen(false)}
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10">
              <img
                src="/logo.png"
                alt="AuthWorka Logo"
                className={`transition-all ${collapsed ? 'h-8 w-8' : 'h-9 w-auto'}`}
              />
            </div>

            {!collapsed && (
              <div className="min-w-0">
                <p className="truncate text-xl font-black leading-tight text-primary">AuthWorka</p>
                <p className="truncate text-xs text-gray-500">Trusted workforce platform</p>
              </div>
            )}
          </Link>

          {!collapsed && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSidebarOpen(false)}
                className="rounded-xl p-2 transition hover:bg-gray-100 lg:hidden"
                aria-label="Close sidebar"
              >
                <X size={22} />
              </button>

              <button
                onClick={() => setCollapsed(true)}
                className="hidden lg:flex h-10 w-10 items-center justify-center rounded-xl transition hover:bg-gray-100"
                aria-label="Collapse sidebar"
              >
                <ChevronLeft size={18} />
              </button>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-6">
          <div className="space-y-2">
            {menu.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    group relative flex items-center rounded-2xl transition-all
                    ${
                      collapsed
                        ? 'mx-auto h-14 w-14 justify-center'
                        : 'gap-4 px-4 py-4'
                    }
                    ${
                      active
                        ? 'bg-primary/10 text-primary shadow-sm'
                        : item.highlight
                        ? 'bg-orange-50 text-orange-700 ring-1 ring-orange-200'
                        : 'text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  <Icon
                    size={collapsed ? 24 : 22}
                    className={
                      active
                        ? 'text-primary'
                        : item.highlight
                        ? 'text-orange-600'
                        : 'text-gray-600'
                    }
                  />

                  {!collapsed && (
                    <>
                      <span className="flex-1 font-medium">{item.name}</span>

                      {item.highlight && (
                        <span className="rounded-full bg-orange-500 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
                          Action Needed
                        </span>
                      )}
                    </>
                  )}

                  {collapsed && (
                    <div className="pointer-events-none absolute left-full ml-3 whitespace-nowrap rounded-xl bg-gray-900 px-3 py-2 text-sm font-medium text-white opacity-0 shadow-lg transition group-hover:opacity-100">
                      {item.name}
                      {item.highlight ? ' (Action Needed)' : ''}
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* User section */}
        <div className="border-t border-gray-200 p-4">
          {collapsed ? (
            <div className="flex flex-col items-center gap-3">
              <Link
                to="/dashboard/profile"
                onClick={() => setSidebarOpen(false)}
                className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-secondary font-bold text-white shadow-lg transition hover:ring-4 hover:ring-primary/20"
                title="View Profile"
              >
                {userImage ? (
                  <img
                    src={userImage}
                    alt={user.first_name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  getUserInitial()
                )}
              </Link>

              <button
                onClick={logout}
                className="group flex h-11 w-11 items-center justify-center rounded-xl text-red-600 transition hover:bg-red-50"
                title="Sign Out"
              >
                <LogOut size={20} className="transition-transform group-hover:scale-110" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Link
                to="/dashboard/profile"
                onClick={() => setSidebarOpen(false)}
                className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-primary to-secondary font-bold text-white shadow-lg transition hover:ring-4 hover:ring-primary/20"
                title="View Profile"
              >
                {userImage ? (
                  <img
                    src={userImage}
                    alt={user.first_name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  getUserInitial()
                )}
              </Link>

              <Link
                to="/dashboard/profile"
                onClick={() => setSidebarOpen(false)}
                className="min-w-0 flex-1"
              >
                <p className="truncate font-semibold text-gray-900 transition hover:text-primary">
                  {user.first_name} {user.last_name}
                </p>
                <p className="text-xs capitalize text-gray-500">
                  {role.replace('_', ' ')}
                </p>
              </Link>

              <button
                onClick={logout}
                className="group rounded-xl p-2.5 text-red-600 transition hover:bg-red-50"
                title="Sign Out"
              >
                <LogOut size={20} className="transition-transform group-hover:scale-110" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main area */}
      <div className={`transition-all duration-300 ${desktopContentOffset}`}>
        {/* Top bar */}
        <header className="fixed left-0 right-0 top-0 z-30 h-16 border-b border-gray-200 bg-white/90 backdrop-blur lg:left-auto">
          <div className="flex h-full items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="rounded-xl p-2 transition hover:bg-gray-100 lg:hidden"
                aria-label="Open sidebar"
              >
                <Menu size={24} />
              </button>

              {collapsed && (
                <button
                  onClick={() => setCollapsed(false)}
                  className="hidden lg:flex h-10 w-10 items-center justify-center rounded-xl transition hover:bg-gray-100"
                  aria-label="Expand sidebar"
                >
                  <ChevronRight size={18} />
                </button>
              )}
            </div>

            <div className="flex items-center gap-3 sm:gap-4">
              <Link
                to="/dashboard/messages"
                className="group relative rounded-xl p-3 transition hover:bg-gray-100"
                onClick={() => setSidebarOpen(false)}
              >
                <MessagesSquare
                  size={23}
                  className="text-gray-700 transition-colors group-hover:text-primary"
                />
                <span className="absolute right-2 top-2 h-2.5 w-2.5 rounded-full bg-red-500" />
              </Link>

              <NotificationsBell />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="page-content min-h-screen px-4 pt-20 sm:px-6 lg:px-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}